import { useQuery } from "../../../react-query";
import { riskLimitsApi } from "../../../services/api";

/**
 * Daily exposure and net P&L per strategy_name within one strategy type (drill-down's Risk Analytics tab).
 * Database only, so it is fast. Returns { dates, strategies: { <name>: [{ date, lmv, smv, net_pnl }] } }.
 */
export function useStrategyRiskHistory(strategyTypeName, startDate, endDate, enabled = true) {
  return useQuery(
    ["riskLimits", "strategy-risk-history", strategyTypeName, startDate, endDate],
    () => riskLimitsApi.getStrategyRiskHistory({ strategy_type_name: strategyTypeName, start_date: startDate, end_date: endDate }),
    {
      enabled: Boolean(enabled && strategyTypeName && startDate && endDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

const PENDING_POLL_MS = 1500;

/**
 * Daily VaR 95%/99% per strategy_name (Risk Monitor), loaded separately from exposure/P&L so the charts
 * never wait on it. The backend answers within a time budget from its cache and reports `pending` days
 * still loading; while any are pending this re-requests every 1.5s, and keeps the previous data on screen
 * (also when switching kind or range) so the chart fills in instead of blanking.
 * kind: "standalone" | "contribution".
 */
export function useStrategyVarHistory(strategyTypeName, startDate, endDate, kind = "standalone", enabled = true) {
  return useQuery(
    ["riskLimits", "strategy-var-history", strategyTypeName, startDate, endDate, kind],
    () => riskLimitsApi.getStrategyVarHistory({ strategy_type_name: strategyTypeName, start_date: startDate, end_date: endDate, kind }),
    {
      enabled: Boolean(enabled && strategyTypeName && startDate && endDate),
      staleTime: 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      refetchInterval: (data) => (data && data.pending > 0 ? PENDING_POLL_MS : false),
    },
  );
}

/**
 * Daily greeks shock P&L per strategy_name within one strategy type (Risk Monitor), one request for the whole range.
 * Returns { scenarios, dates, strategies: { <name>: [{ date, greeks: { <scenario>: pnl } | null }] }, greeks_available }.
 */
export function useStrategyGreeksHistory(strategyTypeName, startDate, endDate, enabled = true) {
  return useQuery(
    ["riskLimits", "strategy-greeks-history", strategyTypeName, startDate, endDate],
    () => riskLimitsApi.getStrategyGreeksHistory({ strategy_type_name: strategyTypeName, start_date: startDate, end_date: endDate }),
    {
      enabled: Boolean(enabled && strategyTypeName && startDate && endDate),
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}
